import PharmacyStatusTag from '@app/icare/components/status/PharmacyStatusTag'
import { PharmacyEditDrawer } from '@app/icare/routes/_app/apotheken/-components/PharmacyEditDrawer'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { Q, QueryBaseSchema } from '@pkg/api/query'
import { defineEntityConfig } from '@pkg/db/schema/EntityConfig'
import { ButtonLink } from '@pkg/ui/button'
import { LinkIndicatorIcon } from '@pkg/ui/common'
import { FormattedNameAndAddress } from '@pkg/ui/formattedContent/NameAndAddress'
import { FaStaffSnake } from 'react-icons/fa6'
import { z } from 'zod'
import { PharmacySchema, PharmacyStatusEnum } from './schema/Pharmacy'

const PharmacyFilterSchema = QueryBaseSchema.extend({
  sortBy: z
    .enum([
      'pharmacy.name',
      'pharmacy.address.streetAddress',
      'pharmacy.address.zipCode',
      'pharmacy.address.city',
      'pharmacy.status',
    ])
    .nullish(),
  filters: z
    .object({
      status: Q.in(z.enum(PharmacyStatusEnum.$.zodEnum())).nullish(),
    })
    .optional(),
})

const label = t`Apotheke`

export const PharmacyConfig = defineEntityConfig(() => ({
  schema: PharmacySchema,

  label,
  labelPlural: t`Apotheken`,

  icon: FaStaffSnake,

  route: {
    list: '/apotheken',
    edit: '/apotheken/$id',
    new: '/apotheken/new',
  },

  drawer: { modal: PharmacyEditDrawer },

  relationSelectionColumns: [
    {
      key: 'pharmacy.name',
      title: label,
      sorter: true,
      render: (_, row) => <FormattedNameAndAddress name={row.name} address={row.address ?? undefined} />,
    },
    {
      key: 'pharmacy.status',
      title: <Trans>Status</Trans>,
      sorter: true,
      render: (_, row) => <PharmacyStatusTag value={row.status} />,
    },
  ],

  indexColumns: [
    {
      key: 'pharmacy.name',
      title: label,
      fixed: 'start',
      sorter: true,
      render: (_, row) => (
        <FormattedNameAndAddress
          name={row.name}
          address={row.address ?? undefined}
          link={{ to: '/apotheken/$id', params: { id: row.id } }}
        />
      ),
    },
    {
      key: 'pharmacy.status',
      title: <Trans>Status</Trans>,
      sorter: true,
      render: (_, row) => <PharmacyStatusTag value={row.status} />,
    },
    {
      key: 'actions',
      title: <Trans>Aktionen</Trans>,
      sorter: false,
      fixed: 'end',
      render: (_, row) => (
        <ButtonLink to="/apotheken/$id" params={{ id: row.id }} size="small" shape="round">
          <Trans>Anzeigen</Trans>
          <LinkIndicatorIcon />
        </ButtonLink>
      ),
    },
  ],

  search: {
    filterSchema: PharmacyFilterSchema,
    sortableFields: ['name', 'status', 'createdAt'] as const,
    defaultSortBy: 'name',
    defaultSortOrder: 'asc',
  },

  format: {
    primary: val => val?.name,
  },
}))
